import { Editor } from '@tiptap/react';
import type { SxProps, Theme } from '@mui/material/styles';

export interface EditorWithRightClick extends Editor {
  showOnRightClick?: boolean;
}

export interface AnchorCoords {
  top: number;
  left: number;
}

export interface ProcessTextResponse {
  result: string;
}

/** Converts raw AI text with `\n\n` paragraph breaks into proper HTML `<p>` nodes. */
export const formatTextToHtml = (text: string): string => {
  if (!text) return '';
  return text
    .split('\n\n')
    .map((para) => {
      const trimmed = para.trim();
      if (!trimmed) return '';
      const escaped = trimmed
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return `<p>${escaped.replace(/\n/g, '<br />')}</p>`;
    })
    .filter(Boolean)
    .join('');
};

/** Resolves the viewport bounding rect of the current text selection or cursor. */
export const getSelectionRect = (editor: Editor): DOMRect => {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) return rect;
  }

  try {
    const { from } = editor.state.selection;
    const coords = editor.view.coordsAtPos(from);
    return new DOMRect(coords.left, coords.top, 0, 0);
  } catch {
    return new DOMRect(0, 0, 0, 0);
  }
};

/** Walks up the DOM from `start` to find the nearest scrollable ancestor. */
export const findScrollParent = (start: HTMLElement | null): HTMLElement | null => {
  let el = start;
  while (el && el !== document.body) {
    const overflow = window.getComputedStyle(el).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
};

export const POPOVER_ROOT_SX: SxProps<Theme> = { pointerEvents: 'none' };

export const POPOVER_PAPER_SX: SxProps<Theme> = {
  pointerEvents: 'auto',
  borderRadius: 2,
  width: 280,
  maxHeight: 'min(480px, calc(100vh - 120px))',
  overflowY: 'auto',
  bgcolor: (theme) =>
    theme.palette.mode === 'dark'
      ? 'rgba(30, 30, 30, 0.95)'
      : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid',
  borderColor: (theme) =>
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.1)',
  boxShadow: (theme) =>
    theme.palette.mode === 'dark'
      ? '0 12px 40px rgba(0,0,0,0.6)'
      : '0 12px 40px rgba(0,0,0,0.15)',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.15)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: (theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.3)',
  },
};
